import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, Button, TextField, Paper, Container } from '@mui/material';
import { Home as HomeIcon, Search as SearchIcon } from '@mui/icons-material';
import PageBackground from '../components/layout/PageBackground';
import SEOHead from '../components/SEO/SEOHead';

const NotFoundPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchTerm, setSearchTerm] = useState('');
    const [countdown, setCountdown] = useState(10);

    // Auto-redirect countdown
    useEffect(() => {
        const timer = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    navigate('/');
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [navigate]);

    const handleSearch = () => {
        if (searchTerm.trim()) {
            navigate(`/quoter?boatSearch=${encodeURIComponent(searchTerm.trim())}`);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    return (
        <PageBackground>
            <SEOHead page="notFound" />
            <Container maxWidth="md" sx={{ minHeight: '100vh' }}>
                <Box
                    sx={{
                        minHeight: '60vh',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    py: 8,
                    textAlign: 'center'
                }}
            >
                {/* 404 Message */}
                <Typography
                    variant="h1"
                    sx={{
                        fontSize: { xs: '4rem', md: '6rem' },
                        fontWeight: 'bold',
                        color: 'primary.main',
                        mb: 2
                    }}
                >
                    404
                </Typography>

                <Typography
                    variant="h4"
                    sx={{
                        mb: 2,
                        color: 'text.primary'
                    }}
                >
                    Page Not Found
                </Typography>

                <Typography
                    variant="body1"
                    sx={{
                        mb: 4,
                        color: 'text.secondary',
                        maxWidth: '600px'
                    }}
                >
                    Sorry, the page you're looking for doesn't exist. It may have been moved or deleted.
                    We've redesigned our site to serve you better!
                </Typography>

                {/* Auto-redirect message */}
                <Paper
                    elevation={2}
                    sx={{
                        p: 2,
                        mb: 4,
                        backgroundColor: 'info.light',
                        maxWidth: '500px',
                        width: '100%'
                    }}
                >
                    <Typography variant="body2" color="info.dark">
                        You'll be redirected to our home page in <strong>{countdown}</strong> seconds...
                    </Typography>
                </Paper>

                {/* Search Box */}
                <Box sx={{ mb: 4, width: '100%', maxWidth: '500px' }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        Looking for replacement sails?
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                        Search for your boat model:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Enter your boat model (e.g., Catalina 270)"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyPress={handleKeyPress}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: 'orange.main',
                                        borderWidth: '2px',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: 'orange.main',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: 'orange.main',
                                    }
                                }
                            }}
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSearch}
                            startIcon={<SearchIcon />}
                            sx={{
                                minWidth: '120px',
                                textTransform: 'none'
                            }}
                        >
                            Search
                        </Button>
                    </Box>
                </Box>

                {/* Navigation Buttons */}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                    <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        startIcon={<HomeIcon />}
                        onClick={() => navigate('/')}
                        sx={{ textTransform: 'none' }}
                    >
                        Go to Home Page
                    </Button>
                    <Button
                        variant="outlined"
                        color="primary"
                        size="large"
                        onClick={() => navigate('/quoter')}
                        sx={{ textTransform: 'none' }}
                    >
                        Browse All Boats
                    </Button>
                </Box>

                {/* Helpful Links */}
                <Box sx={{ mt: 6, width: '100%', maxWidth: '600px' }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        Popular Pages:
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
                        <Button
                            variant="text"
                            onClick={() => navigate('/cruising-sails')}
                            sx={{ textTransform: 'none' }}
                        >
                            Cruising Sails
                        </Button>
                        <Button
                            variant="text"
                            onClick={() => navigate('/performance-sails')}
                            sx={{ textTransform: 'none' }}
                        >
                            Performance Sails
                        </Button>
                        <Button
                            variant="text"
                            onClick={() => navigate('/sail-cloth')}
                            sx={{ textTransform: 'none' }}
                        >
                            Sail Cloth Guide
                        </Button>
                        <Button
                            variant="text"
                            onClick={() => navigate('/contact')}
                            sx={{ textTransform: 'none' }}
                        >
                            Contact Us
                        </Button>
                    </Box>
                </Box>

                {/* Technical Info (small print) */}
                {location.pathname && (
                    <Typography
                        variant="caption"
                        sx={{
                            mt: 4,
                            color: 'text.disabled',
                            fontFamily: 'monospace'
                        }}
                    >
                        Requested URL: {location.pathname}
                    </Typography>
                )}
                </Box>
            </Container>
        </PageBackground>
    );
};

export default NotFoundPage;
